<!-->┌──────────────────────────────────────────────────────────────────────────────────────┐<- ->
<- ->│                                  Reset Password                                      │<- ->
<- ->└──────────────────────────────────────────────────────────────────────────────────────┘<--->

<template>
    <div class="rewards-reset-password">
        <h3 class="rewards-title">{{ newActivation ? "Activate Membership" : "Reset Password" }}</h3>

        <form @submit.prevent="onSubmit()">
            <fieldset class="form-fields" v-bind:disabled="resetLinkInvalid">
                <div class="user-input">
                    <p class="caption" v-if="token">
                        <template v-if="newActivation">
                            Please enter a password for your new account
                        </template>
                        <template v-else>
                            Please enter a new password for your account
                        </template>
                    </p>
                    <labeled-text-input v-else
                                        name="currentPassword" id="currentPassword" class="current-password"
                                        v-model="currentPassword"
                                        type="password"
                                        @change="clearErrors()"
                                        has-icon>Current Password</labeled-text-input>
                    <span v-if="errorDict.currentPassword" class="error">{{ errorDict.currentPassword }}</span>

                    <labeled-text-input name="newPassword" id="newPassword" class="new-password"
                                        v-model="newPassword"
                                        type="password"
                                        @change="clearErrors()"
                                        onkeypress="return event.charCode != 32"
                                        has-icon>{{ newActivation ? "Password" : "New Password" }}</labeled-text-input>
                    <span v-if="errorDict.newPassword" class="error">{{ errorDict.newPassword }}</span>

                    <labeled-text-input name="confirmNewPassword" id="confirmNewPassword" class="confirm-password"
                                        v-model="confirmNewPassword"
                                        type="password"
                                        @change="clearErrors()"
                                        onkeypress="return event.charCode != 32"
                                        has-icon>{{ newActivation ? "Confirm Password" : "Confirm New Password" }}</labeled-text-input>
                    <span v-if="errorDict.confirmNewPassword" class="error">{{ errorDict.confirmNewPassword }}</span>

                    <span v-if="errorDict.MismatchedPasswords" class="error">{{ errorDict.MismatchedPasswords }}</span>
                </div>

                <div class="user-action">
                    <button class="button" name="submit" @click.prevent="onSubmit()">Submit</button>
                </div>
            </fieldset>
        </form>

        <div class="reset-link-invalid" v-if="token && resetLinkInvalid">
            <p class="error">
                This password reset link is no longer valid.<br />
                Click <b>Forgot Password</b> to request a new link.
            </p>
            <button class="button-s" name="submit" @click.prevent="$router.push({path:'/recover'})">Forgot Password</button>
        </div>

        <transition-expand v-if="error || exception" classList="conflicts-container">
            <conflicts :error.sync="error" :exception.sync="exception" :is-sys-admin="isSysAdmin"></conflicts>
        </transition-expand>
    </div>
</template>

<style>
    p.caption {
        padding-bottom: 0.5em;
    }

    .reset-link-invalid {
        display: flex;
        align-items: center;
        column-gap: 1em;
        margin: 2em 2em 0;
        justify-content: space-around;
        flex-flow: row wrap;
        row-gap: 1em;
    }

    fieldset[disabled] p.caption {
        visibility: hidden;
    }

    fieldset[disabled] label {
        color: gray !important;
    }
</style>

<script>
    import { fetchJSON, postJSON } from "@/helpers/fetch"
    import conflicts, { CONFLICTS } from "@/common/conflicts"

    export default {
        components: { conflicts },
        mixins: [CONFLICTS],

        props: ['token','special'],
        emits: ['submitting', 'submitted', 'close'],

        name: 'ResetPassword',
        data() {
            return {
                currentPassword: null,
                newPassword: null,
                confirmNewPassword: null,

                resetLinkInvalid: false,

                errorDict: {},

                submitting: false,
            }
        },

        computed: {
            newActivation() {
                return this.special == 'activate'
            }
        },

        methods: {
            onSubmit() {
                //Validation
                this.errorDict = {};

                if (!this.currentPassword && !this.token)
                    this.errorDict["currentPassword"] = "Current password cannot be left blank.";
                else if (!this.newPassword)
                    this.errorDict["newPassword"] = "New password required.";
                else if (!this.confirmNewPassword)
                    this.errorDict["confirmNewPassword"] = "Confirmation password cannot be left blank.";
                else if (this.newPassword != this.confirmNewPassword)
                    this.errorDict["MismatchedPasswords"] = "New password and confirmation password do not match.";

                if (this.newPassword && this.newPassword.length < 8)
                    this.errorDict["newPassword"] = "New password must be at least 8 characters in length.";

                //If there are no errors in the errorDict, proceed with postJSON to change password
                if (Object.keys(this.errorDict).length === 0) {
                    if (this.submitting) {
                        alert('Processing; please wait ...')
                    }
                    else {
                        this.$emit('submitting')
                        this.submitting = true

                        postJSON('/api/members/ChangePassword', this.token
                            ? { newPassword: this.newPassword, token: this.token }
                            : { newPassword: this.newPassword, pwd: this.currentPassword }
                        )
                            .then(() => {
                                this.$emit('close')
                            })
                            .catch(error => {
                                this.onThrow(error)

                                let errors = this.error && this.error.conflicts
                                if (errors) {
                                    let errorCodes = Object.fromEntries(errors.map(e => [e.code, e]))

                                    let invalidToken = errorCodes[801]
                                    let memberLocked = errorCodes[802]
                                    let memberNotFound = errorCodes[803]
                                    let passwordMismatch = errorCodes[804]

                                    if (memberLocked)
                                        this.error = { title: "Member Account Locked", text: "Your member account has been locked" }

                                    if (passwordMismatch)
                                        this.error = { title: "Password Mismatch", text: "Current password does not match member password" }

                                    if (memberNotFound || invalidToken) {
                                        if (!this.token)
                                            this.$router.push({ path: '/login' })
                                        else {
                                            this.resetLinkInvalid = true
                                            this.error = null
                                        }
                                    }
                                }
                            })
                            .finally(() => {
                                this.$emit('submitted')
                                this.submitting = false
                            })
                    }
                }
            },

            clearErrors() {
                this.errorDict = {};
            }
        }
    }
</script>
